import type { CalculationOut } from "@/api";
import { formatKg, formatMoney, formatPercent } from "@/lib/format";

import styles from "./CalculationScreen.module.css";
import { count } from "./readiness";

/**
 * Six figures the approval is judged on (D-9): a partial purchase total
 * named as partial, the offer total, two margins because brackets carry no
 * purchase price, the brackets' share, and the freight with its weight.
 * All six are the server's; where lines are open the block says what the
 * sums leave out rather than looking whole.
 *
 * The qualifier line is built from counts only and never absorbs a
 * blocker's own sentence into one run-on (controller notes §2): the real
 * `walls_without_measure` message is already a complete sentence
 * (`bom.py`'s own „Wand 3 und Wand 4 ohne Maß."), so it is printed as its
 * own paragraph instead of being spliced into the qualifier's dash.
 *
 * That blocker belongs to rows stored before E-65 (D-14 Fassung 2). Today a
 * wall without a measure is a hint to tick, and the sums are complete — a
 * wall carries no grating, so it leaves nothing out of them; this block says
 * nothing of it. The two lines below stay for the old rows, which still render.
 */
export function TotalsBlock({ calc }: { calc: CalculationOut }) {
  const freight = calc.lines.find((line) => line.kind === "freight") ?? null;
  // Counts lines with no price at all (`vkTotal === null`), not lines that
  // merely carry a `blockedReason` — a soft-gapped line (N-16) still has a
  // price and is not one of the positions the sums leave out.
  const unpriced = calc.lines.filter((line) => line.vkTotal === null).length;
  const walls = calc.blockers.find((blocker) => blocker.code === "walls_without_measure") ?? null;

  // `count()` (fix round 2, item 17) — the same singular/plural helper
  // `readiness.ts` and `CalculationBanner` already use, instead of this
  // file's own inline ternary.
  const qualifiers: string[] = [];
  if (unpriced > 0) qualifiers.push(`ohne ${count(unpriced, "Position", "Positionen")} ohne Preis`);
  if (walls !== null) {
    qualifiers.push(`ohne ${count(walls.wallIndices.length, "Wand", "Wände")} ohne Maß`);
  }

  return (
    <div className={styles.totals}>
      <dl className={styles.totalsList}>
        <div>
          <dt className="label">EK Roste (ohne Konsolen)</dt>
          <dd className="num">{formatMoney(calc.ekGratingsTotal)}</dd>
        </div>
        <div className={styles.totalsMain}>
          <dt className="label">VK gesamt netto</dt>
          <dd className="num">{formatMoney(calc.vkTotal)}</dd>
        </div>
        <div>
          <dt className="label">Marge auf Roste</dt>
          <dd className="num">{formatPercent(calc.marginOnGratingsPct)}</dd>
        </div>
        <div>
          <dt className="label">Marge auf Angebotssumme</dt>
          <dd className="num">{formatPercent(calc.marginOnTotalPct)}</dd>
        </div>
        <div>
          <dt className="label">Konsolenanteil am VK</dt>
          <dd className="num">{formatPercent(calc.consoleShareOfVkPct)}</dd>
        </div>
        <div>
          <dt className="label">Fracht</dt>
          <dd className="num">
            {formatMoney(freight?.vkTotal ?? null)}
            {calc.weightKg !== null ? <span className={styles.totalsSub}> · {formatKg(calc.weightKg)}</span> : null}
          </dd>
        </div>
      </dl>
      {qualifiers.length > 0 ? <p className={styles.totalsNote}>Summen {qualifiers.join(" · ")}.</p> : null}
      {walls !== null ? <p className={styles.totalsNote}>{walls.message}</p> : null}
    </div>
  );
}
